// Page options → Edit profile (Figma 2745:1613; Over limit 2744:1616). Name and photo save to iam (updateProfileCard),
// "about you and links" (≤ 150 code points, spec D6) and talking about (≤ 255, the map shows 60) to main-api
// (updatePersonal, changed fields only). "More about me" (the 12 lists) has no web editor yet: a disabled "Soon" row
// (ruling P16).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { IdCard } from "lucide-react";
import { getMyPersonal, isBioTooLong, updatePersonal, updateProfileCard } from "../../api/personal";
import { AVATAR_ACCEPT, isAvatarImage, uploadImage } from "../../api/upload";
import { getApiErrorMessage } from "../../lib/apiError";
import { invalidatePersonalFeeds } from "../../hooks/useWallFeed";
import { codePointLength } from "../../utils/text";
import logger from "../../lib/logger";
import PersonalModal from "./PersonalModal";
import Img from "../ui/Img";

export const BIO_MAX = 150;
export const TALK_ABOUT_MAX = 255;
export const NAME_MAX = 50;

interface Props {
  onClose(): void;
}

const FIELD =
  "w-full rounded-lg border-0 bg-[var(--bg-elevated)] px-3 py-2.5 text-[15px] text-[var(--foreground)] placeholder:text-[var(--foreground-subtle)] focus:outline-2 focus:outline-[var(--primary-fill)]";

function Field({
  label,
  htmlFor,
  helper,
  helperError,
  counter,
  counterTestId,
  children,
}: {
  label: string;
  htmlFor: string;
  helper?: string | null;
  helperError?: boolean;
  counter: string;
  counterTestId?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5 min-w-0 flex-1">
      <label htmlFor={htmlFor} className="text-[13px] font-medium text-[var(--foreground)]">{label}</label>
      {children}
      <div className="flex gap-2 text-[11px]">
        <span className={`flex-1 ${helperError ? "text-[var(--destructive-text)]" : "text-[var(--foreground-muted)]"}`}>{helper}</span>
        <span data-testid={counterTestId} className={helperError ? "text-[var(--destructive-text)]" : "text-[var(--foreground-muted)]"}>
          {counter}
        </span>
      </div>
    </div>
  );
}

export default function EditProfileModal({ onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const meQ = useQuery({ queryKey: ["myPersonal"], queryFn: getMyPersonal });
  const mine = meQ.data;

  const [form, setForm] = useState({ firstName: "", lastName: "", bio: "", talkAbout: "" });
  const [loaded, setLoaded] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  // Set only when a new avatar was uploaded here (iam takes the file id).
  const [avatarFileId, setAvatarFileId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverBioTooLong, setServerBioTooLong] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!mine || loaded) return;
    setForm({
      firstName: mine.firstName ?? "",
      lastName: mine.lastName ?? "",
      bio: mine.bio ?? "",
      talkAbout: mine.talkAbout ?? "",
    });
    setAvatarUrl(mine.avatar);
    setLoaded(true);
  }, [mine, loaded]);

  const bioLen = codePointLength(form.bio);
  const talkLen = codePointLength(form.talkAbout);
  const bioChanged = loaded && form.bio !== (mine?.bio ?? "");
  const talkChanged = loaded && form.talkAbout !== (mine?.talkAbout ?? "");
  // An unchanged stored bio over 150 still saves (spec D6, ruling P7); a changed one must fit.
  const bioOver = bioLen > BIO_MAX;
  const namesBlank = !form.firstName.trim() || !form.lastName.trim();
  const blockSave = (bioChanged && bioOver) || talkLen > TALK_ABOUT_MAX || namesBlank;
  const showOver = bioOver || serverBioTooLong;
  // The server counted over 150 while this count does not: say so without a number that would be wrong.
  const overText = bioOver
    ? t("personal_over_limit", { defaultValue: "{{n}} characters over the limit", n: bioLen - BIO_MAX })
    : t("personal_bio_too_long", "This is over the limit. Shorten it and try again.");

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => {
    if (k === "bio") setServerBioTooLong(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  async function onAvatar(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isAvatarImage(file)) {
      // Avatars: JPEG, PNG or WebP only (owner 2026-11-01).
      setError(t("wall_photo_bad_type", "Only JPEG, PNG or WebP"));
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const { id, url } = await uploadImage(file);
      setAvatarUrl(url);
      setAvatarFileId(id);
    } catch (err) {
      logger.error("Avatar upload failed", err);
      setError(getApiErrorMessage(err, t("personal_save_failed", "Failed to save profile. Please try again.")));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function save() {
    if (blockSave || saving || !loaded) return;
    setSaving(true);
    setError(null);
    setServerBioTooLong(false);
    // About (main-api) first, then the card (iam), as on Android: a bio_too_long saves nothing.
    let savedAny = false;
    try {
      const about: Record<string, string> = {};
      // "" clears a field; an unchanged field is not sent.
      if (bioChanged) about.bio = form.bio.trim();
      if (talkChanged) about.talkAbout = form.talkAbout.trim();
      if (Object.keys(about).length > 0) {
        await updatePersonal(about);
        savedAny = true;
      }
      await updateProfileCard({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        ...(avatarFileId ? { avatarFileId } : {}),
      });
      savedAny = true;
      onClose();
    } catch (err) {
      logger.error("Save profile failed", err);
      if (isBioTooLong(err)) setServerBioTooLong(true);
      else setError(getApiErrorMessage(err, t("personal_save_failed", "Failed to save profile. Please try again.")));
    } finally {
      setSaving(false);
      // After any part saved: every cached copy of my card and about — my user, my map/street-chat self, the page
      // header, and my posts' author rows.
      if (savedAny) {
        void qc.invalidateQueries({ queryKey: ["me"] });
        void qc.invalidateQueries({ queryKey: ["myPersonal"] });
        void qc.invalidateQueries({ queryKey: ["personal"] });
        void qc.invalidateQueries({ queryKey: ["personalPosts"] });
        void invalidatePersonalFeeds(qc);
      }
    }
  }

  const title = t("personal_edit_profile", "Edit profile");

  return (
    <PersonalModal title={title} testId="edit-profile-modal" width={480} onClose={onClose}>
      <div className="flex flex-col gap-4 px-5 pb-5">
        <div className="flex flex-col items-center gap-1.5">
          <button
            type="button"
            data-testid="edit-avatar-picker"
            aria-label={avatarUrl ? t("personal_change_photo", "Change photo") : t("personal_add_photo", "Add photo")}
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="relative flex items-center justify-center size-24 overflow-hidden rounded-full border-0 p-0 bg-[var(--primary-muted)] cursor-pointer disabled:opacity-60"
          >
            {avatarUrl ? (
              <Img src={avatarUrl} alt={t("personal_avatar_preview", "Avatar preview")} className="size-full object-cover" />
            ) : (
              <span className="text-3xl font-bold text-[var(--primary-on-surface)]">{(form.firstName[0] ?? "?").toUpperCase()}</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-transparent border-0 p-0 text-[13px] font-medium text-[var(--primary-on-surface)] cursor-pointer disabled:opacity-60"
          >
            {avatarUrl ? t("personal_change_photo", "Change photo") : t("personal_add_photo", "Add photo")}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept={AVATAR_ACCEPT}
            hidden
            data-testid="edit-avatar-file-input"
            onChange={onAvatar}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Field label={t("personal_first_name", "First name")} htmlFor="edit-first-name" counter={`${codePointLength(form.firstName)} / ${NAME_MAX}`}>
            <input id="edit-first-name" data-testid="edit-first-name" className={FIELD} maxLength={NAME_MAX} value={form.firstName} onChange={set("firstName")} />
          </Field>
          <Field label={t("personal_last_name", "Last name")} htmlFor="edit-last-name" counter={`${codePointLength(form.lastName)} / ${NAME_MAX}`}>
            <input id="edit-last-name" data-testid="edit-last-name" className={FIELD} maxLength={NAME_MAX} value={form.lastName} onChange={set("lastName")} />
          </Field>
        </div>

        <Field
          label={t("personal_about_links", "About you and links")}
          htmlFor="edit-bio"
          helper={
            showOver
              ? overText
              : t("personal_about_links_helper", "Links become clickable. Type icons as characters.")
          }
          helperError={showOver}
          counter={`${bioLen} / ${BIO_MAX}`}
          counterTestId="edit-bio-counter"
        >
          <textarea
            id="edit-bio"
            data-testid="edit-bio"
            rows={2}
            aria-invalid={showOver || undefined}
            className={`${FIELD} resize-none ${showOver ? "outline-2 outline-[var(--destructive-text)]" : ""}`}
            value={form.bio}
            onChange={set("bio")}
          />
        </Field>

        <Field
          label={t("personal_talking_about", "Talking about")}
          htmlFor="edit-talkabout"
          helper={t("personal_talk_about_helper", "The map shows the first 60 characters.")}
          helperError={talkLen > TALK_ABOUT_MAX}
          counter={`${talkLen} / ${TALK_ABOUT_MAX}`}
          counterTestId="edit-talkabout-counter"
        >
          <textarea
            id="edit-talkabout"
            data-testid="edit-talkabout"
            rows={1}
            className={`${FIELD} resize-none`}
            value={form.talkAbout}
            onChange={set("talkAbout")}
          />
        </Field>

        <button
          type="button"
          data-testid="edit-more-about"
          disabled
          className="flex items-center gap-3 rounded-lg bg-transparent border-0 px-3 py-2.5 text-left cursor-default"
        >
          <IdCard size={20} aria-hidden="true" className="text-[var(--foreground-subtle)]" />
          <span className="flex-1 text-sm text-[var(--foreground-muted)]">{t("personal_more_about_me", "More about me")}</span>
          <span className="text-xs text-[var(--foreground-subtle)]">{t("personal_soon", "Soon")}</span>
        </button>

        {error && (
          <p role="alert" className="m-0 text-sm text-[var(--destructive-text)]">{error}</p>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-full bg-transparent border-0 text-sm font-semibold text-[var(--primary-on-surface)] cursor-pointer hover:bg-[var(--bg-hover)]"
          >
            {t("wall_cancel", "Cancel")}
          </button>
          <button
            type="button"
            data-testid="edit-save"
            disabled={blockSave || saving || uploading || !loaded}
            onClick={save}
            className="h-10 px-6 rounded-full border-0 bg-[var(--primary-fill)] text-sm font-semibold text-[var(--primary-foreground)] cursor-pointer disabled:opacity-45 disabled:cursor-not-allowed"
          >
            {t("save", "Save")}
          </button>
        </div>
      </div>
    </PersonalModal>
  );
}
